import { useState, useEffect } from 'react'
import axios from 'axios'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { ScrollArea } from '../ui/scroll-area'
import { toast } from 'sonner'
import { useLanguage } from '../../context/LanguageContext'
import { useCategories } from '../../context/CategoriesContext'
import { COLOR_OPTIONS } from '../../lib/utils'
import { MEJORA_ICON_OPTIONS, getIconLabel } from '../../lib/iconOptions'
import { 
  Plus, Circle, Dumbbell, Brain, Laptop, CandlestickChart, TrendingUp, 
  Building2, BookOpen, Heart, Star, Target, Zap, Coffee, Music, 
  Palette, Briefcase, GraduationCap, Users, Home, Car, Plane,
  // New icons
  Sparkles, Activity, Moon, Apple, Shield, Clock, BookMarked, Lightbulb,
  PenTool, PencilLine, TreeDeciduous, UsersRound, UserPlus, Flame,
  ListChecks, CalendarDays, RotateCw, Rocket, MessageCircle, Scale
} from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Extended icon map with all mejora diaria icons
const iconMap = {
  // Original icons
  Dumbbell, Brain, Laptop, CandlestickChart, TrendingUp, Building2, BookOpen,
  Heart, Star, Target, Zap, Coffee, Music, Palette, Briefcase, GraduationCap,
  Users, Home, Car, Plane, Circle,
  // New icons
  Sparkles, Activity, Moon, Apple, Shield, Clock, BookMarked, Lightbulb,
  PenTool, PencilLine, TreeDeciduous, UsersRound, UserPlus, Flame,
  ListChecks, CalendarDays, RotateCw, Rocket, MessageCircle, Scale
}

/**
 * Reusable modal for creating/editing Mejora Diaria (Daily Improvement) categories
 * Can be used from SettingsPage and HoyPage
 */
export function MejoraCategoryModal({ 
  open, 
  onOpenChange, 
  editingCategory = null,
  onSuccess
}) {
  const { locale, t } = useLanguage()
  const { addCategory, updateCategory, fetchCategories } = useCategories()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    name: '',
    icon: 'Circle',
    color: '#6b7280'
  })

  // Reset form when modal opens/closes or editing changes
  useEffect(() => {
    if (open) {
      if (editingCategory) {
        setForm({
          name: editingCategory.name,
          icon: editingCategory.icon || 'Circle',
          color: editingCategory.color || '#6b7280'
        })
      } else {
        setForm({
          name: '',
          icon: 'Circle',
          color: '#6b7280'
        })
      }
    }
  }, [open, editingCategory])

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error(t('settings.nameRequired'))
      return
    }

    if (saving) return
    setSaving(true)

    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, form)
        toast.success(t('settings.categoryUpdated'))
      } else {
        await addCategory(form)
        toast.success(t('settings.categoryCreated'))
      }
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      const message = error.response?.data?.detail || t('finance.errorSaving')
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  const SelectedIcon = iconMap[form.icon] || Circle

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {editingCategory 
              ? t('categoryModals.editMejoraCategory')
              : t('categoryModals.newMejoraCategory')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{t('common.name')} *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder={locale === 'es-MX' ? 'Ej: Meditación' : 'E.g.: Meditation'}
              data-testid="mejora-category-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label>{t('common.icon')}</Label>
            <Select 
              value={form.icon} 
              onValueChange={(value) => setForm(prev => ({ ...prev, icon: value }))}
            >
              <SelectTrigger data-testid="mejora-category-icon-select">
                <SelectValue>
                  <div className="flex items-center gap-2">
                    <SelectedIcon className="w-4 h-4" />
                    {getIconLabel(MEJORA_ICON_OPTIONS.find(i => i.id === form.icon), locale) || t('finance.other')}
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <ScrollArea className="h-[280px]">
                  {MEJORA_ICON_OPTIONS.map((icon) => {
                    const IconComponent = iconMap[icon.id] || Circle
                    return (
                      <SelectItem key={icon.id} value={icon.id}>
                        <div className="flex items-center gap-2">
                          <IconComponent className="w-4 h-4" />
                          {getIconLabel(icon, locale)}
                        </div>
                      </SelectItem>
                    )
                  })}
                </ScrollArea>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('common.color')}</Label>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, color }))}
                  className={`w-8 h-8 rounded-full transition-all ${
                    form.color === color ? 'ring-2 ring-offset-2 ring-foreground scale-110' : ''
                  }`}
                  style={{ backgroundColor: color }}
                  data-testid={`mejora-color-${color}`}
                />
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="pt-4 border-t">
            <Label className="text-xs text-muted-foreground">
              {t('common.preview')}
            </Label>
            <div 
              className="mt-2 p-4 rounded-xl flex items-center gap-3"
              style={{ backgroundColor: form.color }}
            >
              <SelectedIcon className="w-8 h-8 text-white" />
              <span className="text-white font-bold">
                {form.name || t('settings.addCategory')}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-mejora-category-btn">
            {saving 
              ? t('common.saving')
              : editingCategory 
                ? t('common.save')
                : t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Reusable modal for creating/editing Mejora Diaria subcategories
 */
export function MejoraSubcategoryModal({
  open,
  onOpenChange,
  categoryId,
  categoryName,
  editingSubcategory = null,
  onSuccess
}) {
  const { locale, t } = useLanguage()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    name: '',
    points_per_log: ''
  })

  useEffect(() => {
    if (open) {
      if (editingSubcategory) {
        setForm({
          name: editingSubcategory.name || '',
          points_per_log: editingSubcategory.points_per_log?.toString() || ''
        })
      } else {
        setForm({ name: '', points_per_log: '' })
      }
    }
  }, [open, editingSubcategory])

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error(t('settings.nameRequired'))
      return
    }

    if (saving) return
    setSaving(true)

    try {
      const payload = {
        name: form.name.trim(),
        points_per_log: form.points_per_log ? parseInt(form.points_per_log) : null
      }
      
      if (editingSubcategory) {
        await axios.put(`${API}/mejora-subcategories/${editingSubcategory.id}`, payload)
        toast.success(t('settings.subcategoryUpdated'))
      } else {
        await axios.post(`${API}/mejora-subcategories`, {
          ...payload,
          category_id: categoryId
        })
        toast.success(t('settings.subcategoryCreated'))
      }
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      const message = error.response?.data?.detail || t('finance.errorSaving')
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editingSubcategory
              ? t('categoryModals.editSubcategory')
              : t('categoryModals.newSubcategory')}
          </DialogTitle>
          <DialogDescription>
            {categoryName}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{t('common.name')} *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder={locale === 'es-MX' ? 'Ej: Pesas, Cardio, Yoga...' : 'E.g.: Weights, Cardio, Yoga...'}
              data-testid="mejora-subcategory-name-input"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label>
              {t('categoryModals.defaultMinutes')}
            </Label>
            <Input
              type="number"
              min={0}
              max={480}
              value={form.points_per_log}
              onChange={(e) => setForm(prev => ({ ...prev, points_per_log: e.target.value }))}
              placeholder={t('categoryModals.minutesSuggested')}
              data-testid="mejora-subcategory-minutes-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-mejora-subcategory-btn">
            {saving
              ? t('common.saving')
              : editingSubcategory
                ? t('common.save')
                : t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Quick add button component for inline category creation
 */
export function QuickAddMejoraCategoryButton({ onCategoryCreated, className = '' }) {
  const { t } = useLanguage()
  const [showModal, setShowModal] = useState(false)

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className={`flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors ${className}`}
        data-testid="quick-add-mejora-category-btn"
      >
        <Plus className="w-3 h-3" />
        {t('settings.addCategory')}
      </button>
      
      <MejoraCategoryModal
        open={showModal}
        onOpenChange={setShowModal}
        onSuccess={onCategoryCreated}
      />
    </>
  )
}

/**
 * Quick add button for subcategories
 */
export function QuickAddMejoraSubcategoryButton({ 
  categoryId, 
  categoryName, 
  onSubcategoryCreated, 
  className = '' 
}) {
  const { t } = useLanguage()
  const [showModal, setShowModal] = useState(false)

  if (!categoryId) return null

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className={`flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors ${className}`}
        data-testid="quick-add-mejora-subcategory-btn"
      >
        <Plus className="w-3 h-3" />
        {t('categoryModals.newSubcategory')}
      </button>
      
      <MejoraSubcategoryModal
        open={showModal}
        onOpenChange={setShowModal}
        categoryId={categoryId}
        categoryName={categoryName}
        onSuccess={onSubcategoryCreated}
      />
    </>
  )
}
